import type { UrlObject } from 'url';

import Link from 'next/link';

import type { TFunction } from 'i18next';
import { useSetAtom } from 'jotai';

import { ReactComponent as LinkSvg } from '@quick/assets/icons/link.svg';
import { ReactComponent as Trash } from '@quick/assets/icons/trash.svg';
import { type NotificationGet } from '@quick/go-api-types/notifications';
import { type GetNotificationsInfo } from '@quick/go-api-types/notifications/info';

import { PAYMENTS_BILLING_MODAL_QUERY_ID } from '@/app/[lang]/(oppl)/operations/balance/pay_bill/BulkLoadModal';
import { ZIP_MASSIVE_MODAL_QUERY } from '@/app/[lang]/(oppl)/operations/config/internationalization/zip-codes/consts';
import { useLangProvider } from '@/components/core/AppLayout';
import { useFetch } from '@/layers/core/deal/services/useFetch';
import { useUser } from '@/layers/core/deal/services/useUser';
import { axios_demand } from '@/layers/demand/data/fetchers/instances';

import { notificationsOpenAtom } from './atoms';
import { useNotificationsContext } from './NotificationsContext';

export function Notification(props: NotificationGet) {
  const { created_at, id, is_read, event, service_id, body, subject } = props;
  const lang = useLangProvider();

  const { token, id: userId } = useUser();
  const { mutateRef, t } = useNotificationsContext();
  const { mutate: mutateInfo } = useFetch<GetNotificationsInfo>({
    url: '/notifications/info',
    auth: true,
    allowNoAuth: true,
  });

  const date = new Date(created_at).toLocaleString();
  const { title, description } = getText(props, t);

  const setOpen = useSetAtom(notificationsOpenAtom);

  const href = getHref({
    event,
    body,
    serviceId: service_id,
    subject,
    lang,
    userId,
  });

  const markAsRead = async () => {
    try {
      await axios_demand.patch(
        `/notifications/${id}/mark_as_read`,
        {},
        { headers: { Authorization: token } }
      );
      mutateRef.current?.();
      mutateInfo();
    } catch (_err) {
      //
    }
  };

  return (
    <div
      className="relative grid grid-cols-[1fr,2rem] gap-x-2 rounded p-2 hover:bg-gray-200
        md:max-w-[20rem] dark:hover:bg-gray-600"
    >
      <button
        className="text-left"
        title="Mark as Read"
        disabled={is_read}
        onClick={markAsRead}
      >
        <span className="">{title}</span>
        <p className="text-sm">{description}</p>
        <p className="text-xs text-gray-500">{date}</p>
      </button>
      <div className="flex flex-col items-center justify-start gap-1">
        <button
          className="focus:ring-brand rounded-full p-1 hover:bg-gray-400 hover:text-red-700
            focus:outline-none focus:ring-1 dark:hover:bg-gray-500"
          onClick={async () => {
            try {
              await axios_demand.delete(`/notifications/${id}`, {
                data: {},
                headers: { Authorization: token },
              });
              mutateRef.current?.();
              mutateInfo();
            } catch (_err) {
              //
            }
          }}
        >
          <Trash />
        </button>
        <NotificationLink
          onClick={() => {
            markAsRead();
            setOpen(false);
          }}
          href={href}
        />
      </div>
      {!is_read && (
        <div className="bg-brand absolute left-0 h-full w-0.5 rounded-l">
          <span className="sr-only">Unread Notification</span>
        </div>
      )}
    </div>
  );
}

function NotificationLink({
  onClick,
  href,
}: {
  onClick: () => void;
  href: string | UrlObject | null;
}) {
  const { t } = useNotificationsContext();

  if (!href) return null;

  return (
    <Link
      onClick={onClick}
      className="focus:ring-brand hover:text-brand rounded-full p-1 hover:bg-gray-400
        focus:outline-none focus:ring-1 dark:hover:bg-gray-500"
      href={href}
      title={t('common:go')}
    >
      <LinkSvg aria-hidden />
    </Link>
  );
}

const getHref = ({
  event,
  lang,
  serviceId,
  body,
  subject,
  userId,
}: {
  event: string | undefined;
  lang: string;
  serviceId: number | undefined;
  body: string;
  subject: string | number;
  userId: number | undefined;
}): string | UrlObject | null => {
  // Added logic to determine if the user is the client for massive files
  const isClientMassive = userId?.toString() === subject.toString();

  switch (event) {
    case 'assigned_responsability':
      return {
        pathname: `/${lang}/operations/services`,
        query: { serviceId },
      };
    case 'bulk_service_loading_has_finished':
    case 'bulk_service_validation_and_calculation_has_finished':
      return {
        pathname: isClientMassive
          ? `/${lang}/platform/massive`
          : `/${lang}/operations/massive`,
        query: { 'massive-mq': body },
      };
    case 'bulk_zip_code_finished':
      return {
        pathname: `/${lang}/operations/massive/zip`,
        query: { [ZIP_MASSIVE_MODAL_QUERY]: body },
      };
    case 'bulk_user_base_rates_finished':
    case 'bulk_user_additional_rates_finished':
    case 'bulk_service_references_finished':
    case 'bulk_money_route_rules_has_finished':
    case 'bulk_reference_user_rates_has_finished': {
      const pathnameMap: Record<typeof event, string> = {
        bulk_user_base_rates_finished: `/${lang}/operations/rates/base-rates`,
        bulk_user_additional_rates_finished: `/${lang}/operations/rates/additional-rates`,
        bulk_service_references_finished: `/${lang}/operations/rates/additional-costs`,
        bulk_money_route_rules_has_finished: `/${lang}/operations/rates/route-rules`,
        bulk_reference_user_rates_has_finished: `/${lang}/operations/rates/base-rate-relation-costs`,
      };
      return {
        pathname: pathnameMap[event],
        query: { massive: body },
      };
    }
    case 'bulk_payment_billing_validation_and_prepare_has_finished':
      return {
        pathname: `/${lang}/operations/balance/pay_bill`,
        query: { [PAYMENTS_BILLING_MODAL_QUERY_ID]: body },
      };
    case 'bulk_consignment_accomplish_finished':
      return {
        pathname: `/${lang}/operations/balance/consignments`,
        query: { 'consignments-ac-massive': body },
      };
    case 'bulk_consignment_bill_finished':
      return {
        pathname: `/${lang}/operations/balance/consignments`,
        query: { 'consignments-billed-massive': body },
      };
    case 'bulk_manifest_accomplish_finished':
      return {
        pathname: `/${lang}/operations/balance/manifests`,
        query: { 'manifest-ac-massive': body },
      };
    case 'bulk_manifest_settle_finished':
      return {
        pathname: `/${lang}/operations/balance/manifests`,
        query: { 'manifest-settled-massive': body },
      };
    case 'bulk_accomplished_approved_finished':
      return body; // In this case, the body contains the direct URL
    default:
      return null;
  }
};

function getText(
  { event, body, subject, service_id }: NotificationGet,
  t: TFunction<['common', 'OpPl']>
) {
  switch (event) {
    case 'bulk_accomplished_approved_finished':
      return {
        title: t('OpPl:notifications.massiveResults.title'),
        description: t('OpPl:notifications.massiveResults.description'),
      };
    case 'assigned_responsability':
      return {
        title: t('OpPl:notifications.responsabilityAcquired.title'),
        description: t(
          'OpPl:notifications.responsabilityAcquired.description',
          {
            id: service_id,
          }
        ),
      };
    case 'deassigned_responsability':
      return {
        title: t('OpPl:notifications.responsabilityRemoved.title'),
        description: t('OpPl:notifications.responsabilityRemoved.description', {
          id: service_id,
        }),
      };
    case 'bulk_service_loading_has_finished':
      return t('OpPl:notifications.bulkServiceLoadingHasFinished');
    case 'bulk_service_validation_and_calculation_has_finished':
      return t('OpPl:notifications.bulkServiceCalc');
    case 'bulk_zip_code_finished':
      return t('OpPl:notifications.bulkZipCodeFinished');
    default:
      return { title: subject, description: body };
  }
}
